import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { avatarColorForId, employmentEndedBeforePeriod, isSickLeaveRow, userInitials } from "./projectPlanUtils";
import { getEmploymentEndDate, isEmploymentEnded } from "../../utils/employmentEndDate";

export function ProjectPlanCrewPanel({
  resourcePlan,
  loading = false,
  selectedUserIds = [],
  onToggleUser,
  onClearSelection,
  onDragResourceStart,
  onDragResourceEnd,
  draggingResourceId,
}) {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const isAdmin = Boolean(currentUser?.roles?.some((role) => role?.name === "admin"));
  const selectedSet = new Set(selectedUserIds.map(String));

  const crew = useMemo(() => {
    const byUser = new Map();
    const periodStart = resourcePlan?.date_from;
    (resourcePlan?.days || []).forEach((day) => {
      (day.users || []).forEach((row) => {
        const id = row.user?.id;
        if (!id) return;
        if (employmentEndedBeforePeriod(row, periodStart)) return;
        const existing = byUser.get(id) || {
          id,
          displayTitle: row.user?.displayTitle || row.user?.name || "",
          hired: row.user?.user_type_id === 3,
          employmentEnds:
            getEmploymentEndDate(row.user) || row.employment_ends || null,
          ended: row.ended === true || row.user?.ended === true,
          plannedHours: 0,
          capacityHours: 0,
          status: row.status,
          absenceLabel: null,
          absentDays: 0,
          weekdays: 0,
          sickWeekdays: 0,
        };
        if (row.ended === true || row.user?.ended === true) existing.ended = true;
        existing.plannedHours += row.planned_event_hours || 0;
        const weekend = day.weekday === "saturday" || day.weekday === "sunday";
        const holiday = Boolean(day.holiday);
        if (!weekend && !holiday) {
          existing.weekdays += 1;
          if (isSickLeaveRow(row)) existing.sickWeekdays += 1;
        }
        if (!isSickLeaveRow(row)) {
          existing.capacityHours += Number(row.scheduled_hours) || 0;
        }
        if (row.status === "clocked_in") existing.status = "clocked_in";
        if (row.status === "absent" || row.absence) {
          existing.status = "absent";
          existing.absentDays += 1;
          const typeTitle =
            row.absence?.type?.title ||
            row.absence?.type?.name ||
            row.absence?.type?.code;
          if (typeTitle) existing.absenceLabel = typeTitle;
        }
        byUser.set(id, existing);
      });
    });
    return Array.from(byUser.values())
      .map((member) => {
        const notCounted = member.weekdays > 0 && member.capacityHours <= 0;
        const notCountedReason =
          notCounted && member.sickWeekdays === member.weekdays ? "sick" : "no_schedule";
        return { ...member, notCounted, notCountedReason };
      })
      .sort((a, b) => a.displayTitle.localeCompare(b.displayTitle, "nb"));
  }, [resourcePlan]);

  const notCounted = crew.filter((member) => member.notCounted);

  return (
    <aside className="res hidden w-[280px] shrink-0 flex-col overflow-hidden border-l border-[#E5E9F0] bg-white xl:flex">
      <div className="res-head flex items-center justify-between px-4 pb-2.5 pt-4">
        <div>
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#6B7682]">
            {t("project.plan.crew.title")}
          </h3>
          <p className="text-[11.5px] text-[#9AA3AF]">
            {t("project.plan.crew.subtitle", { count: crew.length })}
          </p>
          {notCounted.length > 0 ? (
            <p className="mt-2 rounded-lg bg-amber-50 px-2.5 py-2 text-[11.5px] font-medium leading-snug text-amber-950">
              {t("project.plan.crew.not_counted_summary", {
                names: notCounted.map((member) => member.displayTitle).join(", "),
              })}
            </p>
          ) : null}
        </div>
        {selectedUserIds.length > 0 ? (
          <button
            type="button"
            onClick={onClearSelection}
            className="text-xs font-medium text-[#2B6CFF] hover:text-[#1F4ED1]"
          >
            {t("project.plan.resource_bar.clear_selection")}
          </button>
        ) : null}
      </div>

      <div className="res-list flex-1 overflow-y-auto p-3">
        {loading ? (
          <ResourceLoading />
        ) : crew.length === 0 ? (
          <p className="text-sm italic text-[#9AA3AF]">{t("project.plan.crew.empty")}</p>
        ) : (
          <div className="flex flex-col gap-2">
            {crew.map((member) => {
              const isSelected = selectedSet.has(String(member.id));
              const pct =
                member.capacityHours > 0
                  ? Math.min(100, Math.round((member.plannedHours / member.capacityHours) * 100))
                  : 0;
              const avatarBg = avatarColorForId(member.id);
              const barWarn = pct >= 90;

              const isDragging = String(draggingResourceId) === String(member.id);
              return (
                <div
                  key={member.id}
                  className={`pp-res-card pp-res-card-stack ${isSelected ? "selected" : ""} ${isDragging ? "dragging" : ""}`}
                >
                <button
                  type="button"
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData(
                      "application/x-plan-resource-id",
                      String(member.id)
                    );
                    e.dataTransfer.effectAllowed = "copy";
                    onDragResourceStart?.(member);
                  }}
                  onDragEnd={() => onDragResourceEnd?.()}
                  onClick={() => onToggleUser?.(member.id)}
                  className="flex w-full items-center gap-2.5 border-0 bg-transparent p-0 text-left"
                >
                  <span
                    className="pp-av flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold text-white"
                    style={{ backgroundColor: avatarBg }}
                  >
                    {userInitials(member.displayTitle)}
                  </span>
                  <span className="res-body min-w-0 flex-1">
                    <div className="res-name flex items-center gap-1.5 truncate text-[13.5px] font-medium text-[#0B1220]">
                      <span className="truncate">{member.displayTitle}</span>
                      {member.hired ? (
                        <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                          {t("employeeSelector.hired_badge")}
                        </span>
                      ) : null}
                      {member.notCounted ? (
                        <span className="shrink-0 rounded-full bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold text-amber-950">
                          {t("project.plan.crew.not_counted_badge")}
                        </span>
                      ) : null}
                      {member.ended || isEmploymentEnded({ employment_ends: member.employmentEnds }) ? (
                        <span className="shrink-0 rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-800">
                          {t("employment.end_date.ended_badge", {
                            date: member.employmentEnds,
                          })}
                        </span>
                      ) : member.employmentEnds ? (
                        <span className="shrink-0 text-[10px] font-medium text-slate-500">
                          {t("employment.end_date.until", { date: member.employmentEnds })}
                        </span>
                      ) : null}
                    </div>
                    <div className="res-role text-[11.5px] text-[#6B7682]">
                      {member.status === "absent"
                        ? member.absenceLabel
                          ? t("project.plan.crew.absent_with_type", {
                              type: member.absenceLabel,
                              days: member.absentDays,
                            })
                          : t("project.plan.crew.absent_days", {
                              days: member.absentDays,
                            })
                        : t(`hours.resource_plan.status.${member.status}`, {
                            defaultValue: member.status,
                          })}
                    </div>
                    <div className={`pp-res-bar ${barWarn ? "warn" : ""}`}>
                      <span style={{ width: `${pct}%` }} />
                    </div>
                    <div className="res-hours mt-1 flex justify-between text-[11px] tabular-nums">
                      {member.notCounted ? (
                        <span className="font-semibold text-amber-950">
                          {t(
                            member.notCountedReason === "sick"
                              ? "project.plan.crew.not_counted_sick"
                              : "project.plan.crew.not_counted_no_schedule"
                          )}
                        </span>
                      ) : (
                        <>
                          <span className="text-[#9AA3AF]">
                            {member.plannedHours.toFixed(0)}h / {member.capacityHours.toFixed(0)}h
                          </span>
                          <span className="font-semibold text-[#9AA3AF]">{pct}%</span>
                        </>
                      )}
                    </div>
                  </span>
                </button>
                {isAdmin ? (
                  <a
                    href={`/dashboard/users/${member.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t("project.plan.crew.open_user", { name: member.displayTitle })}
                    title={t("project.plan.crew.open_user", { name: member.displayTitle })}
                    className="mt-2 inline-flex items-center gap-1 self-start text-[11px] font-semibold text-[#2B6CFF] hover:text-[#1F4ED1]"
                  >
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    {t("project.plan.crew.open_user_short")}
                  </a>
                ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </aside>
  );
}
